import { Link } from "@tanstack/react-router";
import { TextLink } from "@/components/site/primitives";
import { anchorFor, descriptorFor, type LinkPath } from "@/lib/internal-links";
import { cn } from "@/lib/utils";

/**
 * Related-links block. Anchor text always comes from the internal-link
 * registry, so every page describes a given destination the same way.
 *
 * `inline` is the compact row used at the foot of a section; `cards` gives
 * each destination its descriptor, for the "part of" and hub cross-link
 * blocks where the labels alone read as near-identical.
 */
export function RelatedLinks({
  paths,
  variant = "inline",
  label = "Related pages",
  className,
}: {
  paths: LinkPath[];
  variant?: "inline" | "cards";
  label?: string;
  className?: string;
}) {
  if (paths.length === 0) return null;

  if (variant === "cards") {
    return (
      <nav aria-label={label} className={className}>
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {paths.map((path) => (
            <li key={path}>
              <Link
                to={path}
                className="flex h-full flex-col rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary focus-visible:border-primary"
              >
                <span className="text-[15px] font-semibold text-ink">
                  {anchorFor(path)} <span aria-hidden="true">&rarr;</span>
                </span>
                <span className="mt-2 text-sm leading-relaxed text-body">
                  {descriptorFor(path)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  // Inline rows are in-content links rather than a navigation region: several
  // can appear on one page, and repeated identically-named landmarks make
  // screen-reader landmark lists harder to use.
  return (
    <ul className={cn("flex flex-wrap gap-x-8 gap-y-3 text-[15px]", className)}>
      {paths.map((path) => (
        <li key={path}>
          <TextLink to={path}>{anchorFor(path)}</TextLink>
        </li>
      ))}
    </ul>
  );
}

/**
 * "This page is part of X" link, used on city pages to point up to their
 * regional hub with the hub's canonical anchor text.
 */
export function PartOfHub({ hub, className }: { hub: LinkPath; className?: string }) {
  return (
    <p className={cn("text-[15px] text-body", className)}>
      Part of{" "}
      <Link
        to={hub}
        className="rounded-sm font-semibold text-foreground underline decoration-border-strong underline-offset-4 transition-colors hover:text-primary hover:decoration-primary"
      >
        {anchorFor(hub)}
      </Link>
      . {descriptorFor(hub)}
    </p>
  );
}
